import React from 'react';

import {
  Title,
  Paper,
  Stack,
  Alert,
  Button,
  Group
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';

import CallerForm from '../components/caller/CallerForm';
import { type Caller } from '../components/caller/types';
import ErrorState from '../components/state/ErrorState';
import LoadingState from '../components/state/LoadingState';
import { FormatPhone } from '../utils/format';
import { usePostJSON } from '../utils/request';

interface CallerResponse {
  success: boolean;
  caller: Caller;
}

const EditCaller: React.FC = () => {
  const { id: _id } = useParams<{ id: string }>();
  const id = _id ? parseInt(_id, 10) : undefined;
  const navigate = useNavigate();

  const { data: callerResponse, error, loading } = usePostJSON<CallerResponse>(
    '/api/caller/data_one',
    { id },
    [id]
  );

  const caller = callerResponse?.caller || null;

  if (!id) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Caller</Title>
        <Paper withBorder p="md">
          <ErrorState
            message="Caller ID is required"
            style={{ minHeight: '200px' }}
            title="Invalid Caller ID"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (loading) {
    return (
      <LoadingState 
        message="Loading caller data..." 
        style={{ height: '50vh' }} 
      />
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Caller</Title>
        <Paper withBorder p="md">
          <ErrorState
            message={error.message}
            style={{ minHeight: '200px' }}
            title="Error Loading Caller"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (!caller) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Caller</Title>
        <Paper withBorder p="md">
          <Alert color="red" title="Caller Not Found">
            The requested caller could not be found.
          </Alert>
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/callers')}>
              Back to Callers
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  const handleSuccess = () => {
    void navigate('/callers');
  };

  return (
    <Stack gap="md">
      <Group align="center" justify="space-between">
        <Title order={1}>Edit Caller {caller?.phone && `- ${FormatPhone(caller.phone)}`}</Title>
        <Button variant="outline" onClick={() => void navigate('/callers')}>
          Back to All Callers
        </Button>
      </Group>

      <Paper withBorder p="xl">
        <CallerForm
          initialData={caller}
          mode="edit"
          onCancel={() => void navigate('/callers')}
          onSuccess={handleSuccess}
        />
      </Paper>
    </Stack>
  );
};

export default EditCaller;

